import { LoaderCircle } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

export function Button({ className, variant = 'primary', loading, children, disabled, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'dangerGhost' | 'inverse'; loading?: boolean }) {
  return (
    <button
      className={clsx('inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-bold transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/20 disabled:cursor-not-allowed disabled:opacity-55', {
        'bg-brand text-white shadow-lg shadow-brand/15 hover:-translate-y-0.5 hover:bg-brand-dark': variant === 'primary',
        'border border-ink/10 bg-white text-ink hover:border-brand/40 hover:bg-brand-soft/30': variant === 'secondary',
        'text-ink/65 hover:bg-ink/5 hover:text-ink': variant === 'ghost',
        'bg-red-600 text-white hover:bg-red-700': variant === 'danger',
        'text-red-600 hover:bg-red-50 hover:text-red-700': variant === 'dangerGhost',
        'bg-white text-ink shadow-lg shadow-black/10 hover:-translate-y-0.5 hover:bg-brand-soft hover:text-brand-dark': variant === 'inverse',
      }, className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <LoaderCircle className="size-4 animate-spin" /> : null}
      {children}
    </button>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...props }, ref) {
  return <input ref={ref} className={clsx('min-h-12 w-full rounded-2xl border border-ink/10 bg-white px-4 text-base text-ink outline-none transition placeholder:text-ink/35 focus:border-brand/45 focus:ring-4 focus:ring-brand/10', className)} {...props} />;
});

export function Field({ label, error, hint, children }: { label: string; error?: string; hint?: string; children: ReactNode }) {
  return (
    <label className="grid gap-2 text-sm font-semibold text-ink/75">
      <span>{label}</span>
      {children}
      {error ? <span className="text-xs font-medium text-red-600">{error}</span> : null}
      {!error && hint ? <span className="text-xs font-medium text-ink/45">{hint}</span> : null}
    </label>
  );
}

export function Card({ className, children, tone = 'default' }: { className?: string; children: ReactNode; tone?: 'default' | 'dark' | 'danger' }) {
  return <section className={clsx(
    'rounded-[28px] border p-5 shadow-soft sm:p-6',
    tone === 'default' && 'border-ink/[0.08] bg-white text-ink',
    tone === 'dark' && 'border-white/10 bg-ink text-white',
    tone === 'danger' && 'border-red-200 bg-red-50 text-red-800',
    className,
  )}>{children}</section>;
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={clsx('animate-pulse rounded-2xl bg-ink/[0.07]', className)} />;
}

export function Badge({ children, tone = 'neutral', className }: { children: ReactNode; tone?: 'neutral' | 'success' | 'warning' | 'danger'; className?: string }) {
  return <span className={clsx('inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold', {
    'bg-ink/[0.06] text-ink/65': tone === 'neutral',
    'bg-emerald-100 text-emerald-800': tone === 'success',
    'bg-amber-100 text-amber-800': tone === 'warning',
    'bg-red-100 text-red-700': tone === 'danger',
  }, className)}>{children}</span>;
}
